import * as THREE from 'three'

import { CARRIER } from '../config/constants.js'
import { DEG, FT_TO_M } from '../util/units.js'
import { createDeckTexture, deckToShip } from './deckTexture.js'
import { IFLOLS } from './IFLOLS.js'
import { DECK_OUTLINE } from '../world/deckOutline.js'
import { Wake, bowWave } from './Wake.js'

/**
 * Procedural Nimitz-class carrier.
 *
 * Built from two extruded outlines (flight deck and hull) plus a box-assembled
 * island. Everything that matters for flying the ball — the landing area, the
 * wires, the lens, the drop lights — is positioned from the same constants the
 * recovery maths uses, so the picture out of the canopy and the numbers in the
 * grader can never disagree.
 *
 * Local frame of this group:
 *   +X starboard, +Y up, +Z aft   (so the ship's bow points along -Z)
 * The group is rotated by -heading about Y to place it in the world.
 */

const F = FT_TO_M



// Hull outline at the waterline, [starboard, forward] in feet.
const HULL_OUTLINE = [
  [4, 546],
  [42, 460],
  [60, 330],
  [66, 120],
  [66, -180],
  [58, -380],
  [40, -500],
  [12, -546],
  [-12, -546],
  [-40, -500],
  [-58, -380],
  [-66, -180],
  [-66, 120],
  [-60, 330],
  [-42, 460],
  [-4, 546],
]

function shapeFrom(points) {
  const shape = new THREE.Shape()
  points.forEach(([s, f], i) => {
    if (i === 0) shape.moveTo(s, f)
    else shape.lineTo(s, f)
  })
  shape.closePath()
  return shape
}

/**
 * Extrude a ship-frame outline into local space.
 * The shape is authored in feet in the (starboard, forward) plane; this scales
 * it to metres and rotates it so forward becomes -Z and thickness becomes up.
 */
function extrudeShipShape(points, thicknessFt, topAtFt) {
  const geo = new THREE.ExtrudeGeometry(shapeFrom(points), {
    depth: thicknessFt,
    bevelEnabled: false,
    curveSegments: 1,
  })
  // Local +Z of the extrusion is thickness. Rotating -90 about X sends
  // shape-y (forward) to -Z and thickness to +Y.
  geo.rotateX(-Math.PI / 2)
  geo.translate(0, topAtFt - thicknessFt, 0)
  geo.scale(F, F, F)
  geo.computeVertexNormals()
  return geo
}

export class CarrierModel {
  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'Carrier'

    /**
     * Everything that lives on the SEA rather than on the ship: the wake and
     * the bow wave. A sibling of the hull group, not a child, because it must
     * not inherit the deck's pitch and roll — see _buildWake().
     */
    this.waterGroup = new THREE.Group()
    this.waterGroup.name = 'Carrier water'

    this._buildDeck()
    this._buildHull()
    this._buildIsland()
    this._buildWires()
    this._buildDeckEdgeLights()
    this._buildDropLights()
    this._buildWake()

    this.iflols = new IFLOLS()
    this.iflols.group.position.copy(
      this.deckToLocal(CARRIER.LENS_DECK_X_FT, CARRIER.LENS_DECK_Y_FT, 0),
    )
    // Face the lens back down the angled deck at the approaching aircraft.
    this.iflols.group.rotation.y = CARRIER.ANGLE_DEG * DEG
    this.group.add(this.iflols.group)
  }

  /** Deck frame (feet) -> this group's local space (metres). */
  deckToLocal(x, y, z = 0, out = new THREE.Vector3()) {
    const p = deckToShip(x, y)
    return out.set(p.stbd * F, z * F, -p.fwd * F)
  }

  /** Ship frame (feet) -> this group's local space (metres). */
  shipToLocal(fwd, stbd, up = 0, out = new THREE.Vector3()) {
    return out.set(stbd * F, up * F, -fwd * F)
  }

  /* ------------------------------------------------------------------ */

  _buildDeck() {
    this.deckTexture = createDeckTexture()

    const geo = extrudeShipShape(DECK_OUTLINE, 11, 0)
    const mat = new THREE.MeshStandardMaterial({
      map: this.deckTexture,
      roughness: 0.94,
      metalness: 0.05,
      color: 0xffffff,
    })

    // Sides of the deck slab get a plain grey; only the top carries markings.
    const sideMat = new THREE.MeshStandardMaterial({
      color: 0x3a3d40,
      roughness: 0.9,
      metalness: 0.1,
    })

    // ExtrudeGeometry emits group 0 = caps, group 1 = walls.
    const deck = new THREE.Mesh(geo, geo.groups.length > 1 ? [mat, sideMat] : mat)
    deck.name = 'FlightDeck'
    deck.receiveShadow = true
    deck.castShadow = false
    this.group.add(deck)
    this.deck = deck
    this.deckMaterial = mat
  }

  _buildHull() {
    // Deck sits 63 ft above the waterline and the ship draws 37, so the keel
    // is 100 ft below the deck. The hull starts just under the deck slab.
    const keelFt = -(CARRIER.DECK_HEIGHT_FT + CARRIER.DRAFT_FT)
    const geo = extrudeShipShape(HULL_OUTLINE, -11 - keelFt, -11)
    const mat = new THREE.MeshStandardMaterial({
      color: 0x3f474e,
      roughness: 0.82,
      metalness: 0.25,
    })
    const hull = new THREE.Mesh(geo, mat)
    hull.name = 'Hull'
    hull.receiveShadow = true
    this.group.add(hull)

    // Boot topping straddling the waterline, which is at -63 in this frame.
    const bandGeo = extrudeShipShape(
      HULL_OUTLINE.map(([s, f]) => [s * 1.006, f * 1.002]),
      16,
      -CARRIER.DECK_HEIGHT_FT + 8,
    )
    const bandMat = new THREE.MeshStandardMaterial({ color: 0x1a1210, roughness: 0.95 })
    this.group.add(new THREE.Mesh(bandGeo, bandMat))
  }

  _buildIsland() {
    const island = new THREE.Group()
    island.name = 'Island'

    const steel = new THREE.MeshStandardMaterial({
      color: 0x646a70,
      roughness: 0.78,
      metalness: 0.3,
    })
    const dark = new THREE.MeshStandardMaterial({
      color: 0x30363a,
      roughness: 0.7,
      metalness: 0.4,
    })
    const glass = new THREE.MeshStandardMaterial({
      color: 0x0d1b22,
      roughness: 0.15,
      metalness: 0.6,
    })

    const box = (wFt, hFt, lFt, stbd, up, fwd, mat) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(wFt * F, hFt * F, lFt * F), mat)
      m.position.set(stbd * F, up * F, -fwd * F)
      m.castShadow = true
      m.receiveShadow = true
      island.add(m)
      return m
    }

    // Main structure.
    box(36, 46, 168, 76, 23, 60, steel)
    // Bridge level, glazed and stepped out.
    box(40, 12, 74, 74, 52, 96, glass)
    box(34, 10, 60, 76, 63, 88, steel)
    // Primary flight control, aft-facing.
    box(30, 11, 34, 76, 63, 18, glass)
    // Uptakes.
    box(26, 26, 46, 78, 59, -10, dark)
    // Mast and yardarms.
    box(6, 62, 6, 76, 100, 40, dark)
    box(30, 3, 3, 76, 118, 40, dark)
    box(22, 3, 3, 76, 128, 40, dark)
    // SPY radar faces.
    for (const [s, f, rot] of [
      [58.5, 118, 0],
      [93.5, 118, 0],
      [58.5, 6, Math.PI],
      [93.5, 6, Math.PI],
    ]) {
      const face = new THREE.Mesh(
        new THREE.BoxGeometry(1.6 * F, 15 * F, 15 * F),
        new THREE.MeshStandardMaterial({ color: 0x6d7378, roughness: 0.5, metalness: 0.5 }),
      )
      face.position.set(s * F, 40 * F, -f * F)
      face.rotation.y = rot
      island.add(face)
    }

    this.group.add(island)
    this.island = island
  }

  _buildWires() {
    this.wireMeshes = []
    const mat = new THREE.MeshStandardMaterial({
      color: 0x14161a,
      roughness: 0.55,
      metalness: 0.7,
    })

    for (const wx of CARRIER.WIRES_FT) {
      const a = this.deckToLocal(wx, -48, 0.42)
      const b = this.deckToLocal(wx, 48, 0.42)
      const len = a.distanceTo(b)
      const geo = new THREE.CylinderGeometry(0.055, 0.055, len, 6)
      geo.rotateZ(Math.PI / 2)
      const mesh = new THREE.Mesh(geo, mat)
      mesh.position.copy(a).add(b).multiplyScalar(0.5)
      // Derive the heading from the two endpoints rather than hard-coding the
      // deck angle: a cylinder laid along +X and rotated by theta about Y
      // points at (cos theta, 0, -sin theta), so solve for theta directly.
      // Hard-coding the angle here gets the sign wrong and lays the wires at
      // twice the deck angle, which looks almost right and is not.
      const dx = b.x - a.x
      const dz = b.z - a.z
      mesh.rotation.y = Math.atan2(-dz, dx)
      mesh.castShadow = false
      this.group.add(mesh)
      this.wireMeshes.push(mesh)

      // Support "donuts" that hold the pendant off the deck.
      for (const y of [-30, -10, 10, 30]) {
        const d = new THREE.Mesh(
          new THREE.CylinderGeometry(0.09, 0.09, 0.24, 8),
          new THREE.MeshStandardMaterial({ color: 0x22262a, roughness: 0.8 }),
        )
        d.position.copy(this.deckToLocal(wx, y, 0.12))
        this.group.add(d)
      }
    }
  }

  _buildDeckEdgeLights() {
    const geo = new THREE.SphereGeometry(0.14, 6, 5)
    const white = new THREE.MeshBasicMaterial({ color: 0xf6f2e2 })
    const red = new THREE.MeshBasicMaterial({ color: 0xff4b3a })
    const group = new THREE.Group()

    for (let x = 0; x <= CARRIER.LANDING_AREA_LENGTH_FT; x += 40) {
      for (const [y, mat] of [
        [-46, white],
        [46, red],
      ]) {
        const m = new THREE.Mesh(geo, mat)
        m.position.copy(this.deckToLocal(x, y, 0.5))
        group.add(m)
      }
    }
    this.group.add(group)
    this.deckLights = group
  }

  /**
   * The drop lights on the round-down. Vertical strip of lights on the stern
   * that gives you a lineup reference in the last few seconds — the only thing
   * you can see once the deck edge disappears under the nose.
   */
  _buildDropLights() {
    const group = new THREE.Group()
    const mat = new THREE.MeshBasicMaterial({ color: 0xffd76a })
    for (let i = 0; i < 6; i++) {
      const m = new THREE.Mesh(new THREE.SphereGeometry(0.16, 6, 5), mat)
      m.position.copy(this.deckToLocal(-4, 0, -6 - i * 7))
      group.add(m)
    }
    this.group.add(group)
    this.dropLights = group
  }

  _buildWake() {
    // A Nimitz at thirty knots leaves a mile and a half of white water, and it
    // is the first thing you see of the ship — long before the hull resolves,
    // there is a bright line on the sea pointing at it. Replacing the flat
    // tapering quad this used to be with a real Kelvin wake is the single
    // biggest change to how the world reads.
    // On the WATER group, not the hull group.
    //
    // The hull pitches and rolls with the sea — half a degree of pitch and two
    // thirds of a degree of roll at sea state 2, which is nothing on a ship
    // 1,000 ft long. Applied to a wake that runs 9,000 ft astern and 6,000 ft
    // across, the same half degree swings the far end through eighty feet
    // vertically. The wake would have flapped like a sheet, one corner rising
    // clear of the sea while the other sank beneath it, on a fifteen second
    // period. Water does not pitch with the ship; it is what the ship pitches
    // in.
    this.wake = new Wake({
      lengthFt: 9000,
      beamFt: CARRIER.HULL_BEAM_FT,
      opacity: 0.6,
    })
    this.wake.mesh.position.set(0, 0.6, 546 * F)
    this.waterGroup.add(this.wake.group)

    const bow = bowWave({
      beamFt: CARRIER.HULL_BEAM_FT,
      lengthFt: 420,
      opacity: 0.45,
    })
    bow.position.set(0, 0.65, -470 * F)
    this.waterGroup.add(bow)
    this.bowWaveMesh = bow
  }

  /* ------------------------------------------------------------------ */

  /**
   * Push the carrier's world state onto the scene graph.
   * @param {import('../world/Carrier.js').Carrier} carrier
   */
  sync(carrier) {
    // The water the ship is sitting in: position and heading only, at the
    // waterline, with none of the deck's pitch, roll or heave.
    this.waterGroup.position.set(carrier.position.x, 0.0, carrier.position.z)
    this.waterGroup.rotation.set(0, -carrier.headingDeg * DEG, 0)

    this.group.position.copy(carrier.position)
    this.group.position.y += carrier.heaveM
    this.group.rotation.set(0, -carrier.headingDeg * DEG, 0)
    // Deck motion. Applied after the heading so it reads as pitch and roll of
    // the hull rather than a change of course.
    this.group.rotateZ(-carrier.rollDeg * DEG)
    this.group.rotateX(carrier.pitchDeg * DEG)
  }

  dispose() {
    // The wake owns its own geometry and material; the traverse below would
    // free them a second time.
    this.wake?.dispose()
    this.bowWaveMesh?.material?.map?.dispose()
    this.waterGroup.traverse((o) => {
      if (o !== this.wake?.mesh) {
        o.geometry?.dispose?.()
        o.material?.dispose?.()
      }
    })
    this.group.traverse((o) => {
      if (o.geometry) o.geometry.dispose()
      if (o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material]
        mats.forEach((m) => m.dispose())
      }
    })
    this.deckTexture?.dispose()
  }
}
